/**
 * App shell topbar — spec §F3.
 * "Topbar (54px, green gradient): logo chip (42px white rounded, full logo image) +
 *  wordmark block; nav buttons with icons — Overview, Map Explorer, Analysis, Dashboard,
 *  Data Catalogue, Data Manager (analyst+), Administration (manager) — active = gold
 *  bottom border + subtle white bg; right: country Select, role display (demo switcher
 *  only when `?demo=1`), `EN` button, `?` tour button, avatar menu."
 *
 * Markup and class names are the mockup's `#topbar` (styles in styles/base.css) so the
 * rendered bar is pixel-identical. The nav model (the mockup's `const NAV` plus the
 * route each item maps to and its §I2 roles) lives in app/nav.ts.
 */
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '../lib/icons';
import { asLanguage, DEFAULT_LANGUAGE, otherLanguage, setLanguage } from '../lib/i18n';
import { initials } from '../lib/session';
import {
  COUNTRY_NAMES,
  ROLE_NAMES,
  useAppStore,
  type Country,
  type Role,
} from '../lib/state';
import { activeNavId, visibleNavItems } from './nav';

const COUNTRIES: Country[] = ['ALL', 'BFA', 'MLI', 'NER', 'NGA'];
const ROLES: Role[] = ['public', 'field', 'partner', 'analyst', 'manager'];

export function Topbar() {
  const { t, i18n } = useTranslation();
  /**
   * Read off the i18next instance rather than a store field, so the button re-renders on
   * `languageChanged` — including a change made from somewhere other than this button.
   */
  const language = asLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const country = useAppStore((s) => s.country);
  const setCountry = useAppStore((s) => s.setCountry);
  const role = useAppStore((s) => s.role);
  const setRole = useAppStore((s) => s.setRole);
  const user = useAppStore((s) => s.user);
  const splashOpen = useAppStore((s) => s.splashOpen);
  const setSplashOpen = useAppStore((s) => s.setSplashOpen);
  const analysisOpen = useAppStore((s) => s.analysisOpen);
  const setAnalysisOpen = useAppStore((s) => s.setAnalysisOpen);

  /**
   * §F3: the role control is a demo switcher only under `?demo=1`; otherwise a label.
   * The switcher is **client-only** (§I2 "UI gating … mirror of server rules, never the
   * enforcement"): it re-gates the nav and every role-aware widget for a UAT sweep, and
   * changes nothing about the session the API sees. Real roles come from `/auth/me`.
   */
  const demo = searchParams.get('demo') === '1';

  /**
   * "Map Explorer" owns `/`. "Overview" re-opens the §F4 splash and "Analysis" opens the
   * toolbox dock (§F6); both live on `/` and neither is a route, so `activeNavId` matches
   * on the path alone (see app/nav.ts).
   *
   * **Except while one of their two panels is on screen**: the splash is a full-screen
   * landing card and the dock is 390 px of the map, and leaving Map Explorer highlighted
   * under either (which is what the mockup does) tells the reader they are looking at
   * something they are not. The splash wins when both are up — it is in front.
   */
  const items = visibleNavItems(role);
  const activeId = splashOpen
    ? 'home'
    : analysisOpen && location.pathname === '/'
      ? 'analysis'
      : activeNavId(location.pathname, role);

  /**
   * §F4 "Overview nav re-opens the splash". The nav model marks it `action: true`; every
   * other item is a route and navigates.
   */
  const onNav = (item: (typeof items)[number]) => {
    if (item.id === 'home') {
      setSplashOpen(true);
      return;
    }
    // §F6: "Analysis" is the other action on `/` — it opens the toolbox dock over the Map
    // Explorer, navigating there first if the reader is somewhere else. It never toggles:
    // pressing a nav item is a request to *see* something, and the dock's own ✕ is how it
    // goes away.
    if (item.id === 'analysis') {
      if (splashOpen) setSplashOpen(false);
      if (location.pathname !== '/') navigate('/');
      setAnalysisOpen(true);
      return;
    }
    // Any other tab is a real destination, and the overlay is in front of all of them.
    if (splashOpen) setSplashOpen(false);
    navigate(item.path);
  };

  return (
    <div id="topbar">
      <div className="logo">
        <span className="mark">
          <img src="/logo.png" alt="" width={42} height={42} />
        </span>
        <span>
          {t('app.title')}
          <small>{t('app.tagline')}</small>
        </span>
      </div>

      <nav id="mainnav" aria-label={t('app.title')}>
        {items.map((n) => (
          <button
            key={n.id}
            type="button"
            data-nav={n.id}
            className={n.id === activeId ? 'active' : ''}
            aria-current={n.id === activeId ? 'page' : undefined}
            onClick={() => onNav(n)}
          >
            <Icon name={n.icon} size={16} />
            {t(`nav.${n.key}`)}
          </button>
        ))}
      </nav>

      <div className="spacer" />

      <div className="tb-ctl" title={t('topbar.geography')}>
        <Icon name="globe" size={15} />
        <select
          id="countrySel"
          aria-label={t('topbar.geography')}
          value={country}
          onChange={(e) => setCountry(e.target.value as Country)}
        >
          {COUNTRIES.map((c) => (
            <option key={c} value={c}>
              {t(`country.${c}`, COUNTRY_NAMES[c])}
            </option>
          ))}
        </select>
      </div>

      <div className="tb-ctl" title={t('topbar.role')}>
        <Icon name="shield" size={15} />
        {demo ? (
          <select
            id="roleSel"
            aria-label={t('topbar.role')}
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`role.${r}`, ROLE_NAMES[r])}
              </option>
            ))}
          </select>
        ) : (
          <span id="roleLabel">{t(`role.${role}`, ROLE_NAMES[role])}</span>
        )}
      </div>

      {/* §F3's `EN` button, doing what it says. The label is the language the portal is
          **in** — the mockup's is `EN` on an English page — and the tooltip names the one
          the click would switch to, so the control reads the same way round as the mockup's
          did. `lang` is a per-reader preference (`lib/i18n.ts`): it is remembered in
          `localStorage` and stays out of the permalink, so a shared map does not carry the
          sender's language to the recipient. */}
      <button
        type="button"
        className="tb-ctl"
        id="langBtn"
        lang={language}
        title={t('topbar.switchLanguage')}
        aria-label={t('topbar.switchLanguage')}
        onClick={() => {
          void setLanguage(otherLanguage(language));
        }}
      >
        {t('topbar.lang')}
      </button>

      <button
        type="button"
        className="tb-ctl"
        id="tourBtn"
        title={t('topbar.tour')}
        onClick={() => {
          /* §F11 guided tour — M7. No-op in M1. */
        }}
      >
        ?
      </button>

      {/* §F3 avatar menu. Initials come from the §D1 `name`; with nobody signed in the
          mockup's own persona chip ("AO") is kept rather than an empty or "?" chip. */}
      <div className="avatar" title={user ? user.name : t('topbar.avatar')}>
        {initials(user?.name)}
      </div>
    </div>
  );
}

export default Topbar;
